import { useCallback, useEffect, useRef, useState } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection'
import { mergeRegister } from '@lexical/utils'
import {
  $getNodeByKey,
  $getSelection,
  $isNodeSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  type NodeKey,
} from 'lexical'
import { $isImageNode } from './image-node'

interface ImageComponentProps {
  src: string
  altText: string
  width?: number
  height?: number
  nodeKey: NodeKey
}

export function ImageComponent({ src, altText, width, height, nodeKey }: ImageComponentProps) {
  const [editor] = useLexicalComposerContext()
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey)
  const containerRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const [isResizing, setIsResizing] = useState(false)

  const onDelete = useCallback(
    (event: KeyboardEvent) => {
      const selection = $getSelection()
      if ($isNodeSelection(selection) && isSelected) {
        event.preventDefault()
        const node = $getNodeByKey(nodeKey)
        if ($isImageNode(node)) {
          node.remove()
        }
      }
      return false
    },
    [isSelected, nodeKey]
  )

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand<MouseEvent>(
        CLICK_COMMAND,
        (event) => {
          if (containerRef.current && containerRef.current.contains(event.target as Node)) {
            if (!event.shiftKey) {
              clearSelection()
            }
            setSelected(true)
            return true
          }
          return false
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(KEY_DELETE_COMMAND, onDelete, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_BACKSPACE_COMMAND, onDelete, COMMAND_PRIORITY_LOW)
    )
  }, [editor, clearSelection, setSelected, onDelete])

  const handleResizeStart = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()

      const img = imgRef.current
      if (!img) return

      setIsResizing(true)
      const startX = event.clientX
      const startWidth = img.naturalWidth ? img.offsetWidth : (width ?? img.offsetWidth)
      const aspectRatio = img.naturalHeight
        ? img.naturalWidth / img.naturalHeight
        : startWidth / (img.offsetHeight || 1)

      const onMouseMove = (e: MouseEvent) => {
        const newWidth = Math.max(100, startWidth + (e.clientX - startX))
        const newHeight = Math.round(newWidth / aspectRatio)
        if (imgRef.current) {
          imgRef.current.style.width = `${newWidth}px`
          imgRef.current.style.height = `${newHeight}px`
        }
      }

      const onMouseUp = (e: MouseEvent) => {
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
        setIsResizing(false)

        const finalWidth = Math.max(100, startWidth + (e.clientX - startX))
        const finalHeight = Math.round(finalWidth / aspectRatio)

        editor.update(() => {
          const node = $getNodeByKey(nodeKey)
          if ($isImageNode(node)) {
            node.setDimensions(finalWidth, finalHeight)
          }
        })
      }

      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    },
    [editor, nodeKey, width]
  )

  return (
    <div ref={containerRef} className={`lexical-image-container${isSelected ? ' selected' : ''}`}>
      <img
        ref={imgRef}
        src={src}
        alt={altText}
        width={width}
        height={height}
        className="lexical-image"
        draggable={false}
      />
      {isSelected && !isResizing && (
        <div className="lexical-image-resize-handle" onMouseDown={handleResizeStart} />
      )}
    </div>
  )
}
